import type { ComponentProps, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';
import { TitleInfo } from './Info.js';

/** Raised surface for one group of content (recipe, last shot, account details). */
export function Card({ className, children, ...rest }: ComponentProps<'div'>) {
  return (
    <div className={className ? `card ${className}` : 'card'} {...rest}>
      {children}
    </div>
  );
}

/**
 * A list or value still loading: a calm "…" in its place (no text that flashes for a moment); screen readers hear
 * the label ("Lade …").
 */
export function Pending({ label }: { label: string }) {
  return (
    <p className="hint" role="status">
      <span aria-hidden="true">…</span>
      <span className="visually-hidden">{label}</span>
    </p>
  );
}

/**
 * Card heading row: the card's title left (20 px, sentence case) with an optional explanation behind an (i), an optional
 * link or action right.
 */
export function CardHeader({
  title,
  action,
  info,
}: {
  title: ReactNode;
  action?: ReactNode;
  /** the explanation and the (i)'s label for screen readers */
  info?: { text: ReactNode; label: string; testId?: string } | null;
}) {
  return (
    <div className="card-head">
      <TitleInfo info={info?.text} label={info?.label ?? ''} testId={info?.testId}>
        <h3 className="card-title">{title}</h3>
      </TitleInfo>
      {action}
    </div>
  );
}

export type Tone = 'neutral' | 'ok' | 'heat' | 'warn' | 'danger' | 'cool' | 'accent';

/** Small coloured dot for a state; the text next to it says the same (never colour alone). */
export function StatusDot({ tone }: { tone: Tone }) {
  return <span className={`dot dot-${tone}`} aria-hidden="true" />;
}

export function Chip({
  tone = 'neutral',
  icon,
  className,
  children,
  ...rest
}: ComponentProps<'span'> & { tone?: Tone; icon?: IconName }) {
  return (
    <span className={`chip chip-${tone}${className ? ` ${className}` : ''}`} {...rest}>
      {icon && <Icon name={icon} size="sm" />}
      {children}
    </span>
  );
}

/** Machine connection states (protocol conn.state) -> tone of the connection chip. */
export function connTone(state: string): Tone {
  if (state === 'online') return 'ok';
  if (state === 'reconnecting' || state === 'restarting') return 'warn';
  if (state === 'offline') return 'danger';
  return 'neutral';
}

/** Dot + text, e.g. "online" or "offline, zuletzt gesehen vor 3 min". testid stays on the text element. */
export function ConnChip({ tone, className, children, ...rest }: ComponentProps<'span'> & { tone: Tone }) {
  return (
    <span className={`conn conn-${tone}${className ? ` ${className}` : ''}`}>
      <StatusDot tone={tone} />
      <span {...rest}>{children}</span>
    </span>
  );
}

/** Inline message: warn (data lost, remote stopped), danger (errors), info (neutral). */
export function Banner({
  tone = 'info',
  icon,
  className,
  children,
  ...rest
}: ComponentProps<'p'> & { tone?: 'info' | 'warn' | 'danger'; icon?: IconName }) {
  return (
    <p className={`banner banner-${tone}${className ? ` ${className}` : ''}`} {...rest}>
      {icon && <Icon name={icon} size="sm" />}
      <span>{children}</span>
    </p>
  );
}

export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return <span className={`spinner spinner-${size}`} aria-hidden="true" />;
}
